import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { HomeComponent } from "./front/home/home.component";
import { LoggedInGuard } from "./guards/logged-in.guard";
import { ResetPasswordComponent } from "./front/reset-password/reset-password.component";
import { ConfirmedAccountGuard } from "./guards/confirmed-account.guard";
import { LoginComponent } from "./front/login/login.component";
import { RegisterComponent } from "./front/register/register.component";
import { ForgotPasswordComponent } from "./front/forgot-password/forgot-password.component";
import { BrokerComponent } from "./front/broker/broker.component";
import { ExchangeComponent } from "./front/exchange/exchange.component";
import { FeesPageComponent } from "./front/fees/fees-page/fees-page.component";
import { LogoutComponent } from "./front/logout/logout.component";
import { LoggedOutGuard } from "./guards/logged-out.guard";
import { SupportPageComponent } from "./front/support-page/support-page.component";
import { PageComponent } from "./front/page/page.component";
import { PageNotFoundComponent } from "./front/page-not-found/page-not-found.component";
import { EmailsUnsubscribeComponent } from "./front/emails-unsubscribe/emails-unsubscribe.component";
import { VerifyIpAddressComponent } from "./front/verify-ip-address/verify-ip-address.component";
import { HasWalletGuard } from "./guards/has-wallet.guard";
import { NoWalletComponent } from "./front/no-wallet/no-wallet.component";
import { PricesPageComponent } from "./front/prices-page/prices-page.component";
import { CryptoPageComponent } from "./front/crypto-page/crypto-page.component";
import { GeneralLayoutComponent } from "./layout/general-layout/general-layout.component";
import { AboutUsComponent } from "./front/about-us/about-us.component";
import { CareersComponent } from "./front/careers/careers.component";
import { OtcPageComponent } from "./front/otc-page/otc-page.component";
import { CorporatesPageComponent } from "./front/corporates-page/corporates-page.component";
import { LoggedInCookieGuard } from "./guards/logged-in-cookie-guard";
import { IndividualVerificationGuard } from "./guards/individual-verification.guard";
import { LegacyVerificationGuard } from "./guards/legacy-verification.guard";
import { SupportedTokensComponent } from "./front/supported-tokens/supported-tokens.component";
import { TradingChartPageComponent } from "./front/trading-chart-page/trading-chart-page.component";
import { SecurityPageComponent } from "./front/security-page/security-page.component";
import { BecomeAvipPageComponent } from "./front/become-avip-page/become-avip-page.component";
import { NotificationCenterPageComponent } from "./navigation/app/notification-center/notification-center-page/notification-center-page.component";
import { CampaignHubComponent } from "./front/campaign-hub/campaign-hub.component";
import { EscalationPageComponent } from "./front/escalation-page/escalation-page.component";


const routes: Routes = [
  { path: "", redirectTo: "home", pathMatch: "full" }, // redirect only

  {
    path: "home",
    component: HomeComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "login",
    canActivate: [LoggedOutGuard],
    component: LoginComponent,
    data: { seo: { indexable: false } },
  },

  {
    path: "logout",
    canActivate: [LoggedInGuard],
    component: LogoutComponent,
    data: { seo: { indexable: false, follow: true } },
  },

  {
    path: "register",
    canActivate: [LoggedOutGuard],
    component: RegisterComponent,
    data: { seo: { indexable: false } },
  },

  {
    path: "verify-ip-address",
    canActivate: [LoggedOutGuard],
    component: VerifyIpAddressComponent,
    data: { seo: { indexable: false } },
  },

  {
    path: "forgot-password",
    component: ForgotPasswordComponent,
    data: { seo: { indexable: false } },
  },

  {
    path: "reset-password",
    component: ResetPasswordComponent,
    data: { seo: { indexable: false } },
  },

  {
    path: "email-unsubscribe",
    component: EmailsUnsubscribeComponent,
    data: { seo: { indexable: false, follow: true } },
  },

  {
    path: "prices",
    component: PricesPageComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "prices/:pair",
    component: CryptoPageComponent,
    data: { fullLayout: true, seo: { indexable: true } },
  },

  {
    path: "about-us",
    component: AboutUsComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "careers",
    component: CareersComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "page/referral-program",
    loadChildren: () =>
      import("./affiliate-program/affiliate-program.module").then(
        (m) => m.AffiliateProgramModule,
      ),
    data: { fullLayout: true, seo: { indexable: true } },
  },

  {
    path: "setup",
    title: "Setup",
    canActivate: [LoggedInGuard],
    loadChildren: () =>
      import("./account-setup/account-setup.module").then(
        (m) => m.AccountSetupModule,
      ),
    data: { seo: { indexable: false } },
  },

  {
    path: "",
    component: GeneralLayoutComponent,
    canActivate: [
      LoggedInCookieGuard,
      LoggedInGuard,
      ConfirmedAccountGuard,
      HasWalletGuard,
    ],
    data: { seo: { indexable: false } },
    children: [
      {
        path: "dashboard",
        loadChildren: () =>
          import("./dashboard/dashboard.module").then((m) => m.DashboardModule),
        data: { oldLayout: true },
      },
      {
        path: "dashboard/prices/:pair",
        component: CryptoPageComponent,
        data: { fullLayout: false, oldLayout: true },
      },
      {
        path: "accounts",
        loadChildren: () =>
          import("./accounts/accounts.module").then((m) => m.AccountsModule),
      },
      {
        path: "lite",
        loadChildren: () =>
          import("./core-exchange/core-exchange.module").then(
            (m) => m.CoreExchangeModule,
          ),
      },
      {
        path: "recurring-buy",
        redirectTo: "recurring-buy/new",
        pathMatch: "full",
      },
      {
        path: "recurring-buy/plans",
        loadComponent: () =>
          import("./recurring-buy/recurring-buy-page/recurring-buy-page.component").then(
            (m) => m.RecurringBuyPageComponent,
          ),
      },
      {
        path: "recurring-buy/plans/:id",
        loadComponent: () =>
          import("./recurring-buy/recurring-buy-page/recurring-buy-page.component").then(
            (m) => m.RecurringBuyPageComponent,
          ),
      },
      {
        path: "recurring-buy/new",
        loadComponent: () =>
          import("./recurring-buy/recurring-buy-page/recurring-buy-page.component").then(
            (m) => m.RecurringBuyPageComponent,
          ),
      },
      {
        path: "recurring-buy/:id/transactions",
        loadComponent: () =>
          import("./recurring-buy/orders-page/recurring-buy-orders-page.component").then(
            (m) => m.RecurringBuyOrdersPageComponent,
          ),
      },
      {
        path: "verification",
        loadChildren: () =>
          import("./verification/verification.module").then(
            (m) => m.VerificationModule,
          ),
        canActivate: [LegacyVerificationGuard],
      },
      {
        path: "referral-program",
        loadChildren: () =>
          import("./affiliate-program/affiliate-program.module").then(
            (m) => m.AffiliateProgramModule,
          ),
      },
      {
        path: "settings",
        canActivate: [ConfirmedAccountGuard, HasWalletGuard],
        loadChildren: () =>
          import("./settings/settings.module").then((m) => m.SettingsModule),
      },
      {
        path: "performance",
        canActivate: [ConfirmedAccountGuard, HasWalletGuard],
        loadChildren: () =>
          import("./performance/performance.module").then(
            (m) => m.PerformanceModule,
          ),
      },
      {
        path: "pro",
        title: "Pro",
        canActivate: [
          LoggedInCookieGuard,
          LoggedInGuard,
          ConfirmedAccountGuard,
          HasWalletGuard,
        ],
        loadChildren: () =>
          import("./exchange/exchange.module").then((m) => m.ExchangeModule),
        data: { minifySidebar: true, oldLayout: true },
      },
      {
        path: "pro/pnl",
        canActivate: [ConfirmedAccountGuard, HasWalletGuard],
        loadChildren: () => import("./pnl/pnl.module").then((m) => m.PnlModule),
        data: { minifySidebar: true, oldLayout: true },
      },
      {
        path: "vip",
        loadChildren: () =>
          import("./vip-account/vip/vip.module").then((m) => m.VipModule),
        data: { oldLayout: true, hideThemeButton: true },
      },
      { path: "notifications", component: NotificationCenterPageComponent },
      {
        path: "kyc",
        loadChildren: () => import("./kyc/kyc.module").then((m) => m.KYCModule),
        canActivate: [LoggedInCookieGuard, IndividualVerificationGuard],
      },
    ],
  },
  // {
  //   path: "",
  //   component: MobileWebViewLayoutComponent,
  //   children: [
  //     {
  //       path: 'mubashir',
  //       title: 'mubashir',
  //       canActivate: [LoggedInCookieGuard, LoggedInGuard, ConfirmedAccountGuard, HasWalletGuard, P2PGuard],
  //       loadChildren: () => import('./p2p/p2p.module').then(m => m.P2PModule),
  //     },
  //   ],
  // },

  // Pro chart for mobile
  // TODO: pro/full-chart is kept for backward compatibility with mobile. Remove once old app versions are dupricated
  {
    path: "pro/full-chart",
    component: TradingChartPageComponent,
    canActivate: [LoggedInCookieGuard],
    data: { seo: { indexable: false } },
  },

  {
    path: "trading-chart",
    component: TradingChartPageComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "no-wallet",
    canActivate: [LoggedInGuard, ConfirmedAccountGuard],
    component: NoWalletComponent,
    data: { seo: { indexable: false } },
  },

  {
    path: "support",
    data: { seo: { indexable: true } },
    canActivateChild: [LoggedInCookieGuard],
    children: [
      { path: "", component: SupportPageComponent },
      { path: "escalate", component: EscalationPageComponent },

      { path: ":type", component: SupportPageComponent },
    ],
  },
  // { path: 'invite-and-earn', component: AffiliateProgramComponent},
  // { path: 'trading-competition', component: TradingCompetitionComponent},
  // { path: 'invite-and-win-competition', component: InviteAndEarnCompetitionComponent},
  {
    path: "otc",
    component: OtcPageComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "corporates",
    component: CorporatesPageComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "campaign-hub",
    component: CampaignHubComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "page",
    children: [
      { path: "core", redirectTo: "lite", pathMatch: "full" },
      {
        path: "lite",
        component: BrokerComponent,
        data: { seo: { indexable: true } },
      },
    ],
  },

  {
    path: "page/pro",
    component: ExchangeComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "page/security",
    component: SecurityPageComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "page/fees",
    canActivate: [LoggedInCookieGuard],
    data: { seo: { indexable: true } },
    children: [
      { path: "", redirectTo: "fiat", pathMatch: "full" },
      { path: ":tab", component: FeesPageComponent },
    ],
  },

  {
    path: "page/vip",
    component: BecomeAvipPageComponent,
    canActivate: [LoggedInCookieGuard],
    data: { seo: { indexable: true } },
  },

  {
    path: "page/supported-tokens",
    component: SupportedTokensComponent,
    data: { seo: { indexable: true } },
  },

  {
    path: "page/:key",
    component: PageComponent,
    data: { seo: { indexable: true }, deferTracking: true },
  },
  // Resturcture old links
  // -------------------------

  // old pro fees
  { path: "pro/fees", redirectTo: "/page/fees/fiat", pathMatch: "full" },

  { path: "core", redirectTo: "lite", pathMatch: "full" },
  {
    path: "core/exchange/:mode",
    redirectTo: "lite/exchange/:mode",
    pathMatch: "full",
  },
  {
    path: "core/exchange/details/:id",
    redirectTo: "lite/exchange/details/:id",
    pathMatch: "full",
  },

  { path: "core/dashboard", redirectTo: "/dashboard", pathMatch: "full" },
  { path: "core/accounts", redirectTo: "/accounts", pathMatch: "full" },

  // Fiat Deposit
  {
    path: "core/deposit",
    redirectTo: "/accounts/fiat/deposit",
    pathMatch: "full",
  },
  {
    path: "core/deposit/:id",
    redirectTo: "/accounts/fiat/deposit/:id",
    pathMatch: "full",
  },
  {
    path: "core/deposit/history",
    redirectTo: "/accounts/fiat/deposit/history",
    pathMatch: "full",
  },

  // Fiat Withdrawal
  {
    path: "core/withdraw",
    redirectTo: "/accounts/fiat/withdraw",
    pathMatch: "full",
  },
  {
    path: "core/withdraw/:id",
    redirectTo: "/accounts/fiat/withdraw/:id",
    pathMatch: "full",
  },
  {
    path: "core/withdraw/history",
    redirectTo: "/accounts/fiat/withdraw/history",
    pathMatch: "full",
  },

  // Multisig
  {
    path: "core/transaction/send",
    redirectTo: "/accounts/wallet/send",
    pathMatch: "full",
  },
  {
    path: "core/transaction/send/:id",
    redirectTo: "/accounts/wallet/send/:id",
    pathMatch: "full",
  },
  {
    path: "core/transaction/send/confirm/:id",
    redirectTo: "/accounts/wallet/send/confirm/:id",
    pathMatch: "full",
  },
  {
    path: "core/transaction/receive",
    redirectTo: "/accounts/wallet/receive",
    pathMatch: "full",
  },
  {
    path: "core/transaction/addresses",
    redirectTo: "/accounts/wallet/addresses",
    pathMatch: "full",
  },
  {
    path: "core/transaction/history",
    redirectTo: "/accounts/wallet/history",
    pathMatch: "full",
  },
  {
    path: "core/transaction/details/:id",
    redirectTo: "/accounts/wallet/details/:id",
    pathMatch: "full",
  },

  // Crypto Deposit
  { path: "core/crypto/deposit", redirectTo: "/accounts", pathMatch: "full" },
  {
    path: "core/crypto/deposit/:currency",
    redirectTo: "/accounts/crypto/deposit/:currency",
    pathMatch: "full",
  },
  {
    path: "core/crypto/deposit/history/:currency",
    redirectTo: "/accounts/crypto/deposit/history/:currency",
    pathMatch: "full",
  },
  {
    path: "core/crypto/deposit/details/:id",
    redirectTo: "/accounts/crypto/deposit/details/:id",
    pathMatch: "full",
  },

  // Crypto Withdrawal
  { path: "core/crypto/withdraw", redirectTo: "/accounts", pathMatch: "full" },
  {
    path: "core/crypto/withdraw/:currency",
    redirectTo: "/accounts/crypto/withdraw/:currency",
    pathMatch: "full",
  },
  {
    path: "core/crypto/withdraw/history/:currency",
    redirectTo: "/accounts/crypto/withdraw/history/:currency",
    pathMatch: "full",
  },
  {
    path: "core/crypto/withdraw/details/:id",
    redirectTo: "/accounts/crypto/withdraw/details/:id",
    pathMatch: "full",
  },
  {
    path: "core/crypto/withdraw/confirm/:id",
    redirectTo: "/accounts/crypto/withdraw/confirm/:id",
    pathMatch: "full",
  },

  // Verification
  { path: "core/verify", redirectTo: "/verification", pathMatch: "full" },
  {
    path: "core/verify/tier-one",
    redirectTo: "/verification/tier-one",
    pathMatch: "full",
  },
  {
    path: "core/verify/tier-one/additional-documents",
    redirectTo: "/verification/tier-one/additional-documents",
    pathMatch: "full",
  },
  {
    path: "core/verify/tier-two",
    redirectTo: "/verification/tier-two",
    pathMatch: "full",
  },
  {
    path: "core/verify/tier-two/additional-documents",
    redirectTo: "/verification/tier-two/additional-documents",
    pathMatch: "full",
  },
  {
    path: "core/verify/tier-three",
    redirectTo: "/verification/tier-three",
    pathMatch: "full",
  },
  {
    path: "core/verify/tier-three/additional-documents",
    redirectTo: "/verification/tier-three/additional-documents",
    pathMatch: "full",
  },

  // Fix inviate & earn learn more link on mobile
  {
    path: "invite-and-earn-competition",
    redirectTo: "page/invite-earn-program-guidelines",
  },

  // -------------------------
  // End of resturcture old links

  // Old URLs migration
  {
    path: "front",
    children: [
      { path: "contact", redirectTo: "/support" },
      { path: "sign-in", redirectTo: "/login" },
      { path: "sign-up", redirectTo: "/register" },
      { path: "forgotten-password", redirectTo: "/forgot-password" },
      { path: ":name", redirectTo: "/page/:name" },
      { path: "", redirectTo: "/home", pathMatch: "full" },
    ],
  },

  // redirects & legacy
  {
    path: "**",
    component: PageNotFoundComponent,
    data: { seo: { indexable: false }, notFound: true },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule { }
